import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Trans, useLingui } from "@lingui/macro";
import { BarChart3, FileSpreadsheet, LineChart, Loader2, Sigma, Table2, TrendingDown, Users } from "lucide-react";

import { Button } from "@app/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@app/components/ui/tabs";
import { PopulationPanel, type CatalogueCohort } from "@app/components/studies/PopulationPanel";
import { RegressionAnalysis } from "@app/components/studies/RegressionAnalysis";
import { SurvivalAnalysis } from "@app/components/studies/SurvivalAnalysis";
import { TableAnalysis } from "@app/components/studies/TableAnalysis";
import { TestLibrary } from "@app/components/studies/TestLibrary";
import { WaterfallAnalysis } from "@app/components/studies/WaterfallAnalysis";
import {
  type AnalysisTab,
  type StudyState,
  decodeState,
  encodeState,
} from "@app/components/studies/state";
import { useStudyAnalysis } from "@app/components/studies/useStudyAnalysis";
import API from "@app/services/api";
import { cn } from "@app/lib/utils";
import { fetchCohortFolders, fileCohort, type CohortFolder } from "@app/utils/cohort-folders";
import { downloadPost } from "@app/utils/download-file";
import { useLabReadOnly } from "@app/utils/use-lab-read-only";
import { usePreviewView } from "@app/zustand/preview-view";
import { toast } from "sonner";
import type {
  GroupingRequest,
  RegressionResult,
  StudyScore,
  StudyVariablesData,
  SurvivalResult,
  TableResult,
  WaterfallResult,
} from "@app/types/study";

/**
 * « Études » (PT-115): a population taken from the catalogue, the groups it is split into, and a
 * library of analyses phrased as clinical questions. The test is chosen by the engine from each
 * variable's type and the data, and the page says why. « Comparer » in the catalogue opens this page
 * on the picked cohorts, at the table: a comparison is a study, not a page of its own.
 */
export default function EtudesPage() {
  const { t } = useLingui();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState<StudyState>(() => decodeState(searchParams));
  const [cohorts, setCohorts] = useState<CatalogueCohort[]>([]);
  const [cohortsLoading, setCohortsLoading] = useState(true);
  const [variables, setVariables] = useState<StudyVariablesData | null>(null);
  const [scores, setScores] = useState<StudyScore[] | null>(null);
  const [exporting, setExporting] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const { previewRole, previewMedecin, previewSpecialite } = usePreviewView();
  // The reader's classeurs (PT-162) group the population's cohorts; under « Vue médecin », read only.
  const [folders, setFolders] = useState<CohortFolder[]>([]);
  const readOnly = useLabReadOnly();
  const canFile = !readOnly && !(previewRole === "medecin" && previewMedecin?.id);

  // The URL is the study: every change of state is written back, without a history entry each.
  useEffect(() => {
    setSearchParams({ s: encodeState(state) }, { replace: true });
  }, [state, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    setCohortsLoading(true);
    void (async () => {
      const res = await API.get({ path: "/catalog/cohortes" });
      if (cancelled) return;
      if (res?.ok) setCohorts((res.data?.datasets ?? []) as CatalogueCohort[]);
      setCohortsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [previewRole, previewMedecin?.id, previewSpecialite]);

  useEffect(() => {
    let cancelled = false;
    fetchCohortFolders()
      .then((next) => !cancelled && setFolders(next))
      // Without its classeurs the population is listed flat.
      .catch(() => !cancelled && setFolders([]));
    return () => {
      cancelled = true;
    };
  }, [previewRole, previewMedecin?.id, previewSpecialite]);

  const fileFromPanel = async (cohortId: string, folderId: string | null) => {
    try {
      await fileCohort(cohortId, folderId);
      setCohorts((prev) => prev.map((c) => (c.id === cohortId ? { ...c, folder_id: folderId } : c)));
      const name = cohorts.find((c) => c.id === cohortId)?.name ?? "";
      const folderName = folders.find((f) => f.id === folderId)?.name;
      toast.success(
        folderName ? t`« ${name} » rangé dans « ${folderName} »` : t`« ${name} » retiré de son classeur`,
      );
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : t`Erreur lors de l'enregistrement`);
    }
  };

  const cohortKey = state.cohorts.join(",");
  useEffect(() => {
    if (!cohortKey) {
      setVariables(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      const res = await API.get({ path: "/study/variables", query: { cohortIds: cohortKey.split(",") } });
      if (!cancelled) setVariables(res?.ok ? (res.data as StudyVariablesData) : null);
    })();
    return () => {
      cancelled = true;
    };
  }, [cohortKey]);

  useEffect(() => {
    if (!cohortKey || state.tab !== "waterfall") return;
    let cancelled = false;
    void (async () => {
      const res = await API.get({ path: "/study/scores", query: { cohortIds: cohortKey.split(",") } });
      if (!cancelled) setScores(res?.ok ? ((res.data?.scores ?? []) as StudyScore[]) : []);
    })();
    return () => {
      cancelled = true;
    };
  }, [cohortKey, state.tab]);

  const population = useMemo(() => {
    if (state.cohorts.length === 0) return null;
    const grouping: GroupingRequest =
      state.cohorts.length > 1
        ? { kind: "cohorts" }
        : state.grouping.kind === "variable" && state.grouping.key
          ? state.grouping
          : { kind: "none" };
    if (grouping.kind === "variable" && grouping.threshold == null) {
      const variable = variables?.variables.find((v) => v.key === grouping.key);
      if (variable?.type === "numeric") return null;
    }
    return { cohort_ids: state.cohorts, grouping };
  }, [state.cohorts, state.grouping, variables]);

  const table = useStudyAnalysis<TableResult>(
    "/study/table",
    population && state.tab === "table" ? { ...population, analysis: state.table } : null,
  );
  const survival = useStudyAnalysis<SurvivalResult>(
    "/study/survival",
    population && state.tab === "survival" ? { ...population, analysis: state.survival } : null,
  );
  const regression = useStudyAnalysis<RegressionResult>(
    "/study/regression",
    population && state.tab === "regression" && state.regression
      ? { ...population, analysis: state.regression }
      : null,
  );

  const waterfall = useStudyAnalysis<WaterfallResult>(
    "/study/waterfall",
    population && state.tab === "waterfall" && state.waterfall
      ? { ...population, analysis: state.waterfall }
      : null,
  );

  const update = (patch: Partial<StudyState>) => setState((prev) => ({ ...prev, ...patch }));

  const exportFile = useCallback(
    async (format: "xlsx" | "csv", analysis?: AnalysisTab) => {
      if (!population) return;
      setExporting(analysis ?? format);
      setExportError(null);
      const body: Record<string, unknown> = { ...population };
      if (analysis ? analysis === "table" : true) body.table = state.table;
      if (analysis ? analysis === "survival" : true) body.survival = state.survival;
      if (state.regression && (analysis ? analysis === "regression" : true)) body.regression = state.regression;
      if (state.waterfall && (analysis ? analysis === "waterfall" : true)) body.waterfall = state.waterfall;
      const query: Record<string, string> = { format };
      if (analysis) query.analysis = analysis;
      const status = await downloadPost(
        "/study/export",
        body,
        query,
        format === "xlsx" ? "mage-x-etude.xlsx" : `mage-x-etude-${analysis ?? "table"}.csv`,
      );
      if (status === 403) setExportError(t`Export indisponible : la spécialité consultée est en lecture seule.`);
      else if (status >= 400) setExportError(t`L'export n'a pas pu être généré.`);
      setExporting(null);
    },
    [population, state.table, state.survival, state.regression, state.waterfall, t],
  );

  const studyVariables = variables?.variables ?? [];

  return (
    <div className="min-h-[calc(100vh-4rem)]">
      <div className="mx-auto max-w-[96rem] space-y-6 px-4 pb-12 pt-10 sm:px-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary/80 shadow-md shadow-primary/20">
              <Sigma className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                <Trans>Études statistiques</Trans>
              </h1>
              <p className="text-sm text-slate-500">
                <Trans>
                  Décrire, comparer et analyser vos cohortes — chaque test est choisi selon vos données, et
                  justifié.
                </Trans>
              </p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <TestLibrary />
            <Button
              onClick={() => void exportFile("xlsx")}
              disabled={!population || exporting !== null}
              className="gap-1.5 bg-primary hover:bg-primary/90"
            >
              {exporting === "xlsx" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="h-4 w-4" />
              )}
              <Trans>Exporter l'étude (Excel)</Trans>
            </Button>
          </div>
        </div>
        {exportError && <p className="text-sm text-red-600">{exportError}</p>}

        <div className="grid gap-6 lg:grid-cols-[20rem_1fr]">
          <aside className="h-fit rounded-xl border border-slate-200 bg-white p-4 lg:sticky lg:top-6">
            {cohortsLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : (
              <PopulationPanel
                cohorts={cohorts}
                folders={folders}
                onFile={canFile ? (cohortId, folderId) => void fileFromPanel(cohortId, folderId) : undefined}
                selected={state.cohorts}
                onSelected={(ids) => update({ cohorts: ids })}
                grouping={state.grouping}
                onGrouping={(grouping) => update({ grouping })}
                variables={studyVariables}
              />
            )}
          </aside>

          <main className="min-w-0">
            {state.cohorts.length === 0 ? (
              <EmptyStudy hasCohorts={cohorts.length > 0} />
            ) : state.tab === null ? (
              <AnalysisLibrary onPick={(tab) => update({ tab })} />
            ) : (
              <Tabs value={state.tab} onValueChange={(tab) => update({ tab: tab as AnalysisTab })}>
                <TabsList className="mb-4">
                  <TabsTrigger value="table" className="gap-1.5">
                    <Table2 className="h-4 w-4" />
                    <Trans>Décrire et comparer</Trans>
                  </TabsTrigger>
                  <TabsTrigger value="survival" className="gap-1.5">
                    <LineChart className="h-4 w-4" />
                    <Trans>Délai jusqu'à un événement</Trans>
                  </TabsTrigger>
                  <TabsTrigger value="regression" className="gap-1.5">
                    <BarChart3 className="h-4 w-4" />
                    <Trans>Facteurs associés</Trans>
                  </TabsTrigger>
                  <TabsTrigger value="waterfall" className="gap-1.5">
                    <TrendingDown className="h-4 w-4" />
                    <Trans>Évolution d'un score</Trans>
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="table">
                  <TableAnalysis
                    variables={studyVariables}
                    settings={state.table}
                    onSettings={(next) => update({ table: next })}
                    result={table.data}
                    loading={table.loading}
                    error={table.error}
                    onExportCsv={() => void exportFile("csv", "table")}
                    exporting={exporting === "table"}
                  />
                </TabsContent>
                <TabsContent value="survival">
                  <SurvivalAnalysis
                    settings={state.survival}
                    onSettings={(next) => update({ survival: next })}
                    result={survival.data}
                    loading={survival.loading}
                    error={survival.error}
                    onExportCsv={() => void exportFile("csv", "survival")}
                    exporting={exporting === "survival"}
                  />
                </TabsContent>
                <TabsContent value="regression">
                  <RegressionAnalysis
                    variables={studyVariables}
                    settings={state.regression}
                    onSettings={(next) => update({ regression: next })}
                    result={regression.data}
                    loading={regression.loading}
                    error={regression.error}
                    onExportCsv={() => void exportFile("csv", "regression")}
                    exporting={exporting === "regression"}
                  />
                </TabsContent>
                <TabsContent value="waterfall">
                  <WaterfallAnalysis
                    scores={scores}
                    settings={state.waterfall}
                    onSettings={(next) => update({ waterfall: next })}
                    result={waterfall.data}
                    loading={waterfall.loading}
                    error={waterfall.error}
                    onExportCsv={() => void exportFile("csv", "waterfall")}
                    exporting={exporting === "waterfall"}
                  />
                </TabsContent>
              </Tabs>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

function EmptyStudy({ hasCohorts }: { hasCohorts: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white px-6 py-20 text-center">
      <Users className="mb-4 h-12 w-12 text-slate-300" />
      <h2 className="text-lg font-semibold text-slate-800">
        <Trans>Choisissez la population étudiée</Trans>
      </h2>
      <p className="mt-2 max-w-md text-sm text-slate-500">
        {hasCohorts ? (
          <Trans>
            Cochez une cohorte pour la décrire ou la comparer selon une variable, ou plusieurs cohortes pour les
            comparer entre elles.
          </Trans>
        ) : (
          <Trans>
            Une étude porte sur les cohortes du catalogue. Créez-en une depuis le tableau de bord : appliquez des
            filtres puis « Sauvegarder comme cohorte ».
          </Trans>
        )}
      </p>
      {!hasCohorts && (
        <Button asChild className="mt-4 bg-primary hover:bg-primary/90">
          <Link to="/dashboard">
            <Trans>Aller au tableau de bord</Trans>
          </Link>
        </Button>
      )}
    </div>
  );
}

/** The questions the page answers, before one is asked. */
function AnalysisLibrary({ onPick }: { onPick: (tab: AnalysisTab) => void }) {
  const cards: { tab: AnalysisTab; icon: typeof Table2; title: React.ReactNode; body: React.ReactNode; tests: React.ReactNode }[] = [
    {
      tab: "table",
      icon: Table2,
      title: <Trans>Décrire et comparer les groupes</Trans>,
      body: (
        <Trans>
          Le « Tableau 1 » : effectifs, moyennes, médianes et pourcentages de chaque variable, et le test qui
          convient à chacune.
        </Trans>
      ),
      tests: <Trans>Welch (t, ANOVA) · Mann-Whitney · Kruskal-Wallis · χ² · Fisher · tests de rangs</Trans>,
    },
    {
      tab: "survival",
      icon: LineChart,
      title: <Trans>Délai jusqu'à un événement</Trans>,
      body: (
        <Trans>
          Survie sans reprise après l'intervention, ou délai jusqu'à l'intervention : les patients suivis moins
          longtemps comptent jusqu'à leur dernière visite.
        </Trans>
      ),
      tests: <Trans>Kaplan-Meier · log-rank · hazard ratio</Trans>,
    },
    {
      tab: "regression",
      icon: BarChart3,
      title: <Trans>Facteurs associés à un événement</Trans>,
      body: (
        <Trans>
          Quels facteurs sont liés à une complication ou à une reprise, chacun ajusté sur les autres — et le groupe
          comme premier facteur.
        </Trans>
      ),
      tests: <Trans>Régression logistique · OR brut et ajusté · IC 95 %</Trans>,
    },
    {
      tab: "waterfall",
      icon: TrendingDown,
      title: <Trans>Évolution d'un score après l'acte</Trans>,
      body: (
        <Trans>
          Pour chaque patient opéré, son score avant l'intervention et à l'horizon choisi : qui s'améliore, de
          combien, et quelle part franchit votre seuil.
        </Trans>
      ),
      tests: <Trans>Graphe en cascade · Wilcoxon apparié · comparaison des variations</Trans>,
    },
  ];
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-slate-900">
        <Trans>Quelle question posez-vous ?</Trans>
      </h2>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => (
          <button
            key={card.tab}
            type="button"
            onClick={() => onPick(card.tab)}
            className={cn(
              "group flex flex-col rounded-xl border border-slate-200 bg-white p-5 text-left transition-all",
              "hover:border-primary/50 hover:shadow-lg",
            )}
          >
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <card.icon className="h-5 w-5 text-primary" />
            </div>
            <div className="font-semibold text-slate-900 group-hover:text-primary">{card.title}</div>
            <p className="mt-1 flex-1 text-sm text-slate-500">{card.body}</p>
            <p className="mt-3 text-2xs text-slate-400">{card.tests}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
